/**
 * MODULE: domain/pages/frontend/components/editor/PagesAiBlockView.tsx
 * PURPOSE: Render AI-generated content block in the note editor
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { Spinner } from 'flowbite-react';
import { HiOutlineSparkles } from 'react-icons/hi';
import { pageAiPresetLabel, type PageAiPreset } from '../../utils/pageAiUtils';

export function PagesAiBlockView({ node, selected }: NodeViewProps) {
  const preset = (node.attrs.preset as PageAiPreset) ?? 'summarise';
  const prompt = String(node.attrs.prompt ?? pageAiPresetLabel(preset));
  const response = String(node.attrs.response ?? '');
  const status = String(node.attrs.status ?? 'loading');
  const errorMessage = String(node.attrs.errorMessage ?? '');

  return (
    <NodeViewWrapper
      className={`my-3 ${selected ? 'ring-2 ring-violet-400 ring-offset-2 dark:ring-offset-gray-950' : ''}`}
      data-testid="pages-ai-block"
    >
      <div className="rounded-lg border border-violet-200 bg-violet-50/70 p-4 dark:border-violet-900 dark:bg-violet-950/30">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-violet-800 dark:text-violet-200">
          <HiOutlineSparkles className="h-4 w-4" aria-hidden />
          <span>{pageAiPresetLabel(preset)}</span>
        </div>
        <p className="mb-3 text-xs text-violet-700/80 dark:text-violet-300/80">{prompt}</p>

        {status === 'loading' && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Spinner size="sm" aria-label="Generating AI response" />
            <span>Generating…</span>
          </div>
        )}

        {status === 'error' && (
          <p className="text-sm text-red-600 dark:text-red-400" data-testid="pages-ai-block-error">
            {errorMessage || 'AI generation failed.'}
          </p>
        )}

        {status === 'complete' && (
          <div
            className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-100"
            data-testid="pages-ai-block-response"
          >
            {response}
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
}
